import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import PhysicianListing from './PhysicianListing';
import FauxDropdown from "../form-inputs/FauxDropdown";
import Loader from "../loaders/loader";
import styled from 'styled-components';
import Pagination from "./PhysicianPagination";
import FauxData from './PhysicianFauxData';

const Header = styled.div`
    padding:1.25rem;
    background:#053b83;
    color:#fff;
    
    @media(min-width:48em){
        padding-top:2.875rem;
        padding-bottom:2.875rem;
    }
`

const IconPhysician = styled.svg`
    margin-right:1rem;
    width:35px;
    height:40px;
    fill:#fff;
`

const DirectoryTitle = styled.h1`
    display:flex;
    align-items:center;
    margin:0;
    font-family:'Montserrat', sans-serif;
    font-size:1.75rem;
    font-weight:700;

    @media(min-width:48em){
        font-size:3rem;
        line-height:.875;
    }
`
const Filters = styled.div`
    display:flex;
    flex-direction:column;
    width:100%;
    margin-top:1.25rem;

    & > * {
        flex-grow:1;
    }

    @media(min-width:48em){
        flex-wrap:wrap;
        margin-top:1.5625rem;
        margin-left:-0.625rem;
        margin-right:-0.625rem;
        flex-direction:row;
        align-items:center;
        justify-content:space-between;

        & > * {
            padding:0 0.625rem;

            &:last-child {
                align-self:flex-end;
                flex-shrink:0;
        }
    }

    @media(min-width:72em){
        flex-wrap:nowrap;
    }
`

const SearchLabel = styled.label`
    display:block;
    margin-bottom:10px;
    text-transform:uppercase;
    font-family:'Montserrat', sans-serif;
    font-size:.8125rem;
    font-weight:700;
`

const Search = styled.input`
    width:100%;
    height: 3.125rem;
    border: 1px solid #D5D5D5;
    border-radius: 5px;
    padding:0 .6875rem;
    background-color: #F8F8F8;
    box-sizing: border-box;
    transition:box-shadow .3s ease-in-out;

    &:focus-visible {
        outline:0;
        box-shadow:inset 0 0 0 0.2rem rgba(134,38,51,1);
    }
`

const FilterGroup = styled.div`
    margin-top:1rem;

    &:first-child {
        margin-top:0;
    }

    &.physician-search__actions button + button {
        margin-top:1rem;
    }

    @media(min-width:48em){
        margin-top:0;
        margin-bottom:1rem;

        &.physician-search__actions {
            display:flex;
            justify-content:center;

            button + button {
                margin-top:0;
                margin-left:10px;
            }
        }
    }

    @media(min-width:72em){
        margin-bottom:0;

        &.physician-search__actions {
            justify-content:space-evenly;

            button + button {
                margin-top:0;
                margin-left:10px;
            }
        }
    }
`

const Submit = styled.button`
    display:inline-block;
    width:100%;
    border-radius: 1.8rem;
    border:1px solid #fff;
    padding: 0.2rem 3.3rem;
    background:transparent;
    font-family:'Montserrat', sans-serif;
    font-size: 0.9375rem;
    font-weight:700;
    line-height: 3.2;
    color:#fff;
    cursor:pointer;
    transition:color .3s ease-in-out, background .3s ease-in-out;

    &:hover, &:focus-visible {
        background:#fff;
        color:#053b83;
    }

    @media(min-width:48em){
        width:auto;
    }
`

const Reset = styled.button`
    display:inline-block;
    width:100%;
    border-radius: 1.8rem;
    border:1px solid #fff;
    padding: 0.2rem 3.3rem;
    background:#fff;
    font-family:'Montserrat', sans-serif;
    font-size: 0.9375rem;
    font-weight:700;
    line-height: 3.2;
    color:#fff;
    cursor:pointer;
    transition:color .3s ease-in-out, background .3s ease-in-out;

    &:hover, &:focus-visible {
        background:#fff;
        color:#053b83;
    }

    svg {
        width:24px;
        height:24px;
        fill:#862633;
    }

    @media(min-width:48em){
        width:auto;
    }
`

const ToolRow = styled.div`
    margin:1.5rem 0 2.625rem;

    @media(min-width:72em){
        display:flex;
        align-items:center;
        margin-top:2.5rem;

        .pagination {
            margin:0 0 0 auto;
        }
    }
`

const NumOfResults = styled.div`
    font-family:'Montserrat', sans-serif;
    font-size: 0.9375rem;
    font-weight:700;
    text-transform:uppercase;
`

const PhysicianDirectory = (props) => {
    const containerElement = document.getElementById("PhysicianDirectory");
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isFiltered, setIsFiltered] = useState(false);
    const [resetDropdowns, setResetDropdowns] = useState(false);

    const [allPhysicians, setAllPhysicians] = useState([]);
    const [physicians, setPhysicians] = useState([]);
    const [services, setServices] = useState([]);
    const [genders, setGenders] = useState([]);
    const [cities, setCities] = useState([]);
    const [selections, setSelections] = useState([]);

    const [keyword, setKeyword] = useState('');
    const [numOfResults, setNumOfResults] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    const detailUrl = containerElement.getAttribute('data-detail-url');
    const itemsPerPage = containerElement.getAttribute('data-items-per-page') != null ? containerElement.getAttribute('data-items-per-page') : 10;
    const urlFields = '$select=Title,Id,UrlName,Gender';
    const urlExpansions = '$expand=Image($select=Url),ServicesList($select=Title,Id),Locations($select=Title,DisplayTitle,Id,Address($select=StateCode,City,Zip,Street))';
    const urlOrder = '$orderby=LastName';
    const url = '/sfapi/physicians/physicians?' + urlFields + '&' + urlExpansions + '&' + urlOrder;

    const removeSelection = (selectionTitle, newValue) => {
        const objectIndex = selections.findIndex(s => s.Title === selectionTitle)

        if(objectIndex > -1 && selections[objectIndex].Value !== newValue){
            const newSelections = selections.filter(function(value, index){ return index !== objectIndex; });
            setSelections(newSelections);
        }
    }

    const selectedService = (serviceSelection) => {
        removeSelection('Service', serviceSelection);
        setSelections(selections => [...selections, {'Title': 'Service', 'Value': serviceSelection}]);
    }

    const selectedGender = (genderSelection) => {
        removeSelection('Gender', genderSelection);
        setSelections(selections => [...selections, {'Title': 'Gender', 'Value': genderSelection}]);
    }

    const selectedCity = (citySelection) => {
        removeSelection('City', citySelection);
        setSelections(selections => [...selections, {'Title': 'City', 'Value': citySelection}]);
    }

    const populateFilters = (items) => {
        let servicesArray = [];
        let gendersArray = [];
        let citiesArray = [];

        items.forEach(element => {
            const ServicesList = element.ServicesList;
            const gender = element.Gender;
            const Locations = element.Locations;

            if(ServicesList.length > 0){
                ServicesList.forEach(element => {
                    if(servicesArray.findIndex(s => s.Value === element.Id) <= -1){
                        servicesArray.push({"Label": element.Title, "Value": element.Id});
                    }
                });
            }

            if(gender !== null && gendersArray.findIndex(g => g.Label === gender) <= -1){
                gendersArray.push({"Label": gender, "Value": gender});
            }

            if(Locations.length > 0){
                Locations.forEach(element => {
                    if(citiesArray.findIndex(c => c.Label === element.Address.City) <= -1){
                        citiesArray.push({"Label": element.Address.City, "Value": element.Address.City});
                    }
                });
            }
        });

        if(servicesArray.length > 0) setServices(servicesArray);
        if(gendersArray.length > 0) setGenders(gendersArray);
        if(citiesArray.length > 0) setCities(citiesArray);
    }

    const renderResults = (data) => {
        if(data){
            setCurrentPage(0);
            setPhysicians(data);
            setNumOfResults(data.length);

            setTimeout(function(){
                setCurrentPage(1);
            }, 300);
        }
    }

    const triggerFiltering = () => {
        const selectionLength = selections.length;

        if(selectionLength > 0){
            let curatedPhysicians = [];

            selections.forEach(selection => {
                switch(selection.Title){
                    case 'Service':
                        const curatedByService = allPhysicians.filter(function(physician){ return physician.ServicesList.some(s => s.Id === selection.Value) });
                        curatedByService.forEach((element) => {
                            if(curatedPhysicians.findIndex(p => p.Id === element.Id) <= -1){
                                curatedPhysicians.push(element);
                            }
                        });
                        break;
                    case 'Gender':
                        const curatedByGender = allPhysicians.filter(function(physician){ return physician.Gender === selection.Value });

                        curatedByGender.forEach((element) => {
                            if(curatedPhysicians.findIndex(p => p.Id === element.Id) <= -1){
                                curatedPhysicians.push(element);
                            }
                        });
                        break;
                    case 'Search':
                        const curatedBySearch = allPhysicians.filter(function(physician){
                            return physician.Title.indexOf(selection.Value) > -1 || physician.Title.toLowerCase().indexOf(selection.Value) > -1;
                        });
                        curatedBySearch.forEach((element) => {
                            if(curatedPhysicians.findIndex(p => p.Id === element.Id) <= -1){
                                curatedPhysicians.push(element);
                            }
                        });
                        break;
                    case 'City':
                        const curatedByCity = allPhysicians.filter(function(physician){return physician.Locations.some(l => l.Address.City === selection.Value)});
                        curatedByCity.forEach((element) => {
                            if(curatedPhysicians.findIndex(p => p.Id === element.Id) <= -1){
                                curatedPhysicians.push(element);
                            }
                        });
                        break;
                }
            });

            renderResults(curatedPhysicians)

            setIsFiltered(true);
        }


        setIsSearching(false);
    }

    const handleSearch = () => {
        removeSelection('Search', keyword);

        if(keyword !== ''){
            setSelections(selections => [...selections, {'Title': 'Search', 'Value': keyword}]);
        }

        setIsSearching(true);
    }

    const reset = () => {
        setCurrentPage(0);
        setKeyword('');
        setSelections([]);
        setResetDropdowns(!resetDropdowns);

        document.getElementById('physicianSearch').value = '';

        setPhysicians(allPhysicians);
        setNumOfResults(allPhysicians.length);
        setIsFiltered(false);

        setTimeout(function(){
            setCurrentPage(1);
        }, 300);
    }

    // Initial Load
    useEffect(() => {
        fetch(url)
        .then(res => res.json())
        .then(
            (data) => {
                const items = data.value;
                setIsLoaded(true);

                setAllPhysicians(items);
                setPhysicians(items);
                populateFilters(items);
                setNumOfResults(items.length);
            },
            (error) => {
                setIsLoaded(true);
                setError(error);
            }
        ).then(() => { setCurrentPage(1); })
    }, []);

    // Initial Load for Local Testing
    // useEffect(() => {
    //     const items = FauxData.value;

    //     setIsLoaded(true);

    //     setAllPhysicians(items);
    //     setPhysicians(items);
    //     populateFilters(items);
    //     setNumOfResults(items.length);
    //     setCurrentPage(1)
    // }, []);

    //When Search Is submitted
    useEffect(() => {
        if(isSearching){
            triggerFiltering();
        }
    },[isSearching]);

    const paginatedPhysicians = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * itemsPerPage;
        const lastPageIndex = firstPageIndex + itemsPerPage;

        return physicians.slice(firstPageIndex, lastPageIndex);
    }, [currentPage]);

    if (error) {
        return <div>Error: {error.message}</div>;
    } 
    else if (!isLoaded) {
        return <Loader></Loader>
    }
    else {
        return ReactDOM.createPortal (
            <div className="physician-directory">
                <Header className="physician-directory__header">
                    <div className="container">
                        <DirectoryTitle className="physician-directory__header-title">
                            <IconPhysician viewBox="0 0 51 56">
                                <path d="M37.0147556,31.2834667 C38.3438222,32.6299556 39.2161778,34.4275556 39.3866667,36.4224 C41.1830222,36.8249778 42.5313778,38.4309333 42.5313778,40.3473778 C42.5313778,42.5674667 40.7256889,44.3731556 38.5056,44.3731556 C36.2855111,44.3731556 34.4798222,42.5680889 34.4798222,40.3473778 C34.4798222,38.4657778 35.7796444,36.8859556 37.5268444,36.4466667 C37.2984889,34.3012444 36.0192,32.4663111 34.2141333,31.4664 L24.9809778,40.1177778 L15.6022222,31.3295111 C13.5184,32.3574222 12.068,34.4704889 11.9896,36.9282667 C12.3486222,37.1367111 12.6379556,37.4596444 12.8159111,37.8485333 C14.1387556,38.4732444 15.3079111,39.7867556 16.2020444,41.6584 C16.3526222,41.9738667 16.3712889,42.3291556 16.2617778,42.6539556 C16.7981333,44.0352889 17.1048889,45.5441778 17.1048889,46.8110222 C17.1048889,48.5868444 17.1048889,50.2656 15.1691556,50.6968 C14.9594667,50.8716444 14.6975111,50.9668444 14.4218667,50.9668444 L13.1288889,50.9668444 C12.4799111,50.9668444 11.9516444,50.4385778 11.9516444,49.7902222 L11.956,49.7068444 C11.9995556,49.0989333 12.5141333,48.6136 13.1288889,48.6136 L14.4218667,48.6136 C14.5537778,48.6136 14.6838222,48.6353778 14.8082667,48.6801778 C14.8860444,48.6571556 14.9115556,48.6397333 14.9115556,48.6397333 C15.0559111,48.384 15.0559111,47.3212444 15.0559111,46.8110222 C15.0559111,45.7824889 14.7983111,44.5399111 14.3465778,43.3832 C14.1126222,43.2500444 13.9234667,43.0490667 13.8064889,42.8051556 C13.0218667,41.1631111 11.956,40.1028444 11.0892444,40.1028444 C10.2032,40.1028444 9.08382222,41.2496 8.30355556,42.9544889 C8.176,43.2326222 7.952,43.4597333 7.67697778,43.5935111 C7.26942222,44.6985778 7.04542222,45.836 7.04542222,46.8104 C7.04542222,47.2384889 7.04542222,48.3771556 7.20968889,48.6434667 C7.21155556,48.6434667 7.24826667,48.6652444 7.35031111,48.6907556 C7.48408889,48.6397333 7.62782222,48.6129778 7.77155556,48.6129778 L9.06702222,48.6129778 C9.66497778,48.6129778 10.1652444,49.0640889 10.2336889,49.6527111 L10.2399111,49.6657778 L10.2424,49.7435556 C10.2424,50.4379556 9.71475556,50.9662222 9.0664,50.9662222 L7.77093333,50.9662222 C7.51395556,50.9662222 7.26506667,50.8803556 7.06097778,50.7235556 C6.32115556,50.5848 5.79973333,50.2581333 5.46808889,49.7267556 C5.06924444,49.084 4.99582222,48.2402667 4.99582222,46.8097778 C4.99582222,45.5535111 5.28515556,44.1093333 5.81217778,42.7286222 C5.7344,42.4330667 5.76053333,42.1244444 5.88933333,41.8469333 C6.43813333,40.6479111 7.13502222,39.6206222 7.90595556,38.8783111 C8.32906667,38.4701333 8.78391111,38.1422222 9.26053333,37.9026667 C9.43973333,37.4839111 9.75146667,37.1404444 10.1316444,36.9239111 C10.1851556,34.7890667 11.0425778,32.8558222 12.4145778,31.4091556 C5.27955556,33.2235556 0,39.6859556 0,47.3859556 C0,55.5961778 6.00008889,56 13.8562667,56 C14.7130667,56 15.5922667,55.9950222 16.4901333,55.9950222 L33.4730667,55.9950222 C34.3659556,55.9950222 35.2464,56 36.1032,56 C43.9593778,56 49.9613333,55.5974222 49.9613333,47.3859556 C49.9607111,39.4949333 44.4173333,32.9043556 37.0147556,31.2834667 Z" id="Path"></path>
                                <circle id="Oval" cx="24.9803556" cy="14.2445333" r="14.2445333"></circle>
                                <circle id="Oval" cx="38.5049778" cy="40.3473778" r="1.85795556"></circle>
                            </IconPhysician>
                            Find a Physician
                        </DirectoryTitle>
                        
                        <Filters className="physician-directory__filters">
                            <FilterGroup role="form">
                                <SearchLabel htmlFor="physicianSearch">
                                    Search by Name
                                </SearchLabel>
                                <Search 
                                    type="text" 
                                    id="physicianSearch" 
                                    placeholder="enter name..." 
                                    onChange={Event => setKeyword(Event.target.value)} 
                                    onKeyDown={Event => Event.key === 'Enter' && handleSearch()}/>
                            </FilterGroup>
                            {services.length > 0 &&
                                <FilterGroup>
                                    <FauxDropdown 
                                        label="Filter by Service" 
                                        placeholder="select service..." 
                                        options={services} 
                                        key={resetDropdowns}
                                        onSelection={selectedService} />
                                </FilterGroup>
                            }
                            {genders.length > 0 &&
                                <FilterGroup>
                                    <FauxDropdown 
                                        label="Filter by Gender" 
                                        placeholder='select gender...' 
                                        options={genders} 
                                        key={resetDropdowns}
                                        onSelection={selectedGender} />
                                </FilterGroup>
                            }
                            {cities.length > 0 && 
                                <FilterGroup>
                                    <FauxDropdown 
                                        label="Filter by City" 
                                        placeholder="select city..."
                                        options={cities} 
                                        key={resetDropdowns}
                                        onSelection={selectedCity} />
                                </FilterGroup>
                            }
                            <FilterGroup className="physician-search__actions">
                                <Submit type="button" aria-label="Submit physician search" onClick={handleSearch}>Search</Submit>
                                {isFiltered &&
                                    <Reset type="button" aria-label="Reset physician search filters" onClick={reset}>
                                        <svg className="icon icon-reload" focusable="false" aria-hidden="true" viewBox="0 0 32 32">
                                            <path d="M24 18c0 4.41-3.586 8-8 8s-8-3.59-8-8 3.586-8 8-8l2.359 0.027-1.164 1.164 2.828 2.828 6.012-6.019-6.012-6-2.828 2.828 1.18 1.172h-2.375c-6.625 0-12 5.371-12 12s5.375 12 12 12 12-5.371 12-12h-4z"></path>
                                        </svg>
                                    </Reset>
                                }
                            </FilterGroup>
                        </Filters>
                    </div>
                </Header>
                
                <div className="container">
                    <ToolRow className="physician-directory__tool-row">
                        <NumOfResults className="physician-directory__results-num">{`${numOfResults} Results Found`}</NumOfResults>
                        <Pagination 
                            className="pagination"
                            currentPage={currentPage}
                            totalCount={physicians.length}
                            pageSize={itemsPerPage}
                            onPageChange={page => setCurrentPage(page)}
                            >
                        </Pagination>
                    </ToolRow>
                </div>

                <div className="container">
                    <PhysicianListing physicians={paginatedPhysicians} detailUrl={detailUrl}></PhysicianListing>
                </div>
            </div>,
            containerElement
        );
    }
}

export default PhysicianDirectory;